// ============================================================
// Shared web notification sound (customer / creator / kasir)
// Respects user preference play_sound + notification_sound.
// ============================================================

import axios from '../config/axios.js';
import { API_BASE } from '../config/axios.js';

const state = {
  loaded: false,
  play: true,
  sound: 'uspatih_notif',
  available: ['uspatih_notif'],
  urls: {},
};

function truthyPlay(v) {
  return v !== false && v !== 0 && v !== '0' && v !== 'false';
}

function soundUrl(name) {
  if (!name) return null;
  if (state.urls[name]) return state.urls[name];
  return `/sounds/${name}.wav`;
}

function beepFallback() {
  try {
    const AudioCtx = window.AudioContext || window.webkitAudioContext;
    if (!AudioCtx) return;
    const ctx = new AudioCtx();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(880, ctx.currentTime);
    osc.frequency.exponentialRampToValueAtTime(440, ctx.currentTime + 0.18);
    gain.gain.setValueAtTime(0.0001, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.12, ctx.currentTime + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.25);
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.start();
    osc.stop(ctx.currentTime + 0.28);
  } catch {
    /* ignore autoplay */
  }
}

export function getNotificationSoundState() {
  return { ...state };
}

export function setNotificationSoundPrefs({ play, sound, available, urls } = {}) {
  if (typeof play === 'boolean') state.play = play;
  if (typeof sound === 'string' && sound) state.sound = sound;
  if (Array.isArray(available) && available.length) state.available = available;
  if (urls && typeof urls === 'object') state.urls = { ...state.urls, ...urls };
  state.loaded = true;
}

export async function loadNotificationSoundPrefs() {
  try {
    const { data } = await axios.get(`${API_BASE}/notifications/settings`);
    const payload = data.data || {};
    const prefs = payload.preferences || {};
    const sounds = payload.sounds || {};
    setNotificationSoundPrefs({
      play: truthyPlay(prefs.play_sound ?? true),
      sound: String(prefs.notification_sound || sounds.default || 'uspatih_notif'),
      available: Array.isArray(sounds.available) && sounds.available.length
        ? sounds.available
        : [sounds.default || 'uspatih_notif'],
      urls: sounds.urls || {},
    });
  } catch {
    state.loaded = true;
  }
  return getNotificationSoundState();
}

/**
 * Play inbox / in-app cue. Skip when user muted (unless force).
 */
export function playNotificationSound({ force = false } = {}) {
  if (!force && state.loaded && !state.play) return;

  const url = soundUrl(state.sound);
  if (url && typeof Audio !== 'undefined') {
    try {
      const audio = new Audio(url);
      audio.volume = 0.7;
      const p = audio.play();
      if (p && typeof p.catch === 'function') {
        p.catch(() => beepFallback());
      }
      return;
    } catch {
      /* fall through */
    }
  }
  beepFallback();
}

export async function saveNotificationSoundPrefs(patch) {
  const body = {};
  if (typeof patch.play_sound === 'boolean') body.play_sound = patch.play_sound;
  if (typeof patch.notification_sound === 'string') body.notification_sound = patch.notification_sound;

  const { data } = await axios.post(`${API_BASE}/notifications/settings`, body);
  const prefs = data.data || {};
  setNotificationSoundPrefs({
    play: truthyPlay(prefs.play_sound ?? patch.play_sound ?? state.play),
    sound: String(prefs.notification_sound || patch.notification_sound || state.sound),
  });
  return prefs;
}
